import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom"; // Added useSearchParams
import { betriebstageBuchService } from "../services/api"; // Combined imports
import { pdfService } from "../services/pdfService";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import moment from "moment";
import SignatureCanvas from "react-signature-canvas";

// Material UI components
import {
  Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField,
  Tooltip, Typography, Alert, AlertTitle, Collapse, useMediaQuery, Menu, ListItemIcon, ListItemText,
  List, ListItem, useTheme, FormHelperText
} from "@mui/material";

// Material UI icons
import {
  AddCircleOutline as AddCircleIcon, FilterList as FilterListIcon, ClearOutlined as ClearIcon,
  Search as SearchIcon, VisibilityOutlined as VisibilityIcon, EditOutlined as EditIcon, CheckCircleOutline as CheckCircleIcon,
  MoreVert as MoreVertIcon, PictureAsPdfOutlined as PictureAsPdfIcon,
  CloseOutlined as CloseDialogIcon, DescriptionOutlined as DescriptionIcon, BorderColorOutlined as SignIcon,
  ListAltOutlined as ListAltIcon, UnfoldMore as UnfoldMoreIcon, AccountCircleOutlined as AccountCircleIcon,
  FilterAltOff as FilterAltOffIcon, WarningAmber as WarningIcon,
  BuildOutlined as ConveyorIcon, PlaylistAddCheckOutlined as SpecialCheckIcon,
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";
import ContentSection from "../components/ContentSection";
import FilterBar from "../components/FilterBar";

// Custom hook for debouncing
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// --- Helper functions ---
function getStatusChip(status, t) {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return { color: 'success', label: t('approved') };
      case 'pending':
        return { color: 'warning', label: t('pending') };
      case 'rejected':
        return { color: 'error', label: t('rejected') };
      case 'draft':
        return { color: 'default', label: t('draft') };
      default:
        return { color: 'default', label: status || t('unknown') };
    }
  };

  const config = getStatusConfig(status);
  return (
    <Chip
      label={config.label}
      size="small"
      color={config.color}
      variant="filled"
    />
  );
}

function hasIncompleteChecks(entry) {
  // Replace with your real logic
  return false;
}

function renderCheckStatusIcon(checked) {
  // Replace with your real logic
  return checked ? <span>✔️</span> : <span>❌</span>;
}

const BetriebstageBuchList = () => {
  const { isManager, isAdmin, currentUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const muiTheme = useTheme(); // Renamed to avoid conflict with custom theme context if any
  const navigate = useNavigate();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();


  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    searchTerm: searchParams.get("search") || "",
    status: searchParams.get("status") || "",
    creatorId: searchParams.get("creatorId") || "",
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "date");
  const [sortDirection, setSortDirection] = useState(searchParams.get("sortDir") || "desc");

  const [pagination, setPagination] = useState({
    currentPage: parseInt(searchParams.get("page") || "0"),
    pageSize: parseInt(searchParams.get("limit") || "10"),
    totalItems: 0,
  });

  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [pdfLoadingEntryId] = useState(null); // Track loading state per entry
  const [uniqueCreators, setUniqueCreators] = useState([]);

  // Debounced search term for instant search
  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);

  const signatureRef = useRef(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [entryToApprove, setEntryToApprove] = useState(null);
  const [signatureData, setSignatureData] = useState(null); // Storing actual signature data
  const [useProfileSigForApproval, setUseProfileSigForApproval] = useState(true);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState(null);

  useEffect(() => {
    if (currentUser?.signature) setUseProfileSigForApproval(true);
    else setUseProfileSigForApproval(false);
  }, [currentUser]);

  const fetchEntries = useCallback(async (isInitialLoad = false) => {
    console.log('🚀 fetchEntries called:', { isInitialLoad, debouncedSearchTerm, 'entries.length': entries.length });

    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: pagination.pageSize,
        page: pagination.currentPage + 1,
        sortBy: sortBy || 'date',
        sortDirection: sortDirection || 'desc',
      };

      // Add filters only if they have values
      if (filters.status) params.status = filters.status;
      if (filters.creatorId) params.creatorId = filters.creatorId;
      if (filters.startDate) params.fromDate = filters.startDate;
      if (filters.endDate) params.toDate = filters.endDate;

      // Only add search if there's actually a search term
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      console.log('📡 [BetriebstageBuchList] Query Params:', params);
      const res = await betriebstageBuchService.getAll(params);
      console.log('📦 [BetriebstageBuchList] Full API Response:', res);
      console.log('📦 [BetriebstageBuchList] res.data type:', typeof res.data);
      console.log('📦 [BetriebstageBuchList] res.data keys:', res.data ? Object.keys(res.data) : 'null/undefined');

      // Try different possible data structures
      let entriesArray = [];
      if (res.data?.data && Array.isArray(res.data.data)) {
        // New API structure: { data: { data: [...], pagination: {...} } }
        entriesArray = res.data.data;
        console.log('✅ Using res.data.data (new structure), length:', entriesArray.length);
      } else if (res.data?.entries) {
        entriesArray = res.data.entries;
        console.log('✅ Using res.data.entries, length:', entriesArray.length);
      } else if (res.data?.betriebstageBuch) {
        entriesArray = res.data.betriebstageBuch;
        console.log('✅ Using res.data.betriebstageBuch, length:', entriesArray.length);
      } else if (Array.isArray(res.data)) {
        entriesArray = res.data;
        console.log('✅ Using res.data directly (array), length:', entriesArray.length);
      } else {
        console.warn('❌ [BetriebstageBuchList] Unexpected API response structure:', res.data);
        entriesArray = [];
      }

      console.log('🎯 [BetriebstageBuchList] Final entries array:', entriesArray);
      console.log('🎯 [BetriebstageBuchList] About to call setEntries with:', entriesArray.length, 'entries');

      setEntries(entriesArray);
      setPagination(prev => ({
        ...prev,
        totalItems: res.data?.pagination?.total || res.data?.total || entriesArray.length
      }));

      console.log('✅ [BetriebstageBuchList] setEntries called successfully');

      if (isInitialLoad) {
        try {
          const creatorsRes = await betriebstageBuchService.getUniqueCreators();
          if (creatorsRes.data) setUniqueCreators(creatorsRes.data);
        } catch (creatorErr) {
          console.error('Error fetching creators:', creatorErr);
        }
      }

    } catch (err) {
      console.error('💥 Error fetching entries:', err);
      setError(t("errorFetchingEntries") + (err.message ? `: ${err.message}` : ''));
    }
    finally {
      console.log('🏁 [BetriebstageBuchList] fetchEntries completed, setting loading to false');
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.currentPage, filters.status, filters.creatorId, filters.startDate, filters.endDate, sortBy, sortDirection, debouncedSearchTerm, t, entries.length]);

  // Initial load and data fetching
  useEffect(() => {
    fetchEntries(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Fetch when any dependency changes (search, filters, sorting, pagination)
  useEffect(() => {
    console.log('🔄 Search effect triggered:', {
      debouncedSearchTerm,
      'filters.searchTerm': filters.searchTerm,
      'pagination.currentPage': pagination.currentPage
    });

    // Reset to page 1 when search term changes
    if (debouncedSearchTerm !== filters.searchTerm) {
      console.log('🔄 Resetting pagination due to search change');
      setPagination(prev => ({ ...prev, currentPage: 0 }));
      return;
    }

    console.log('🔄 About to call fetchEntries from search effect');
    fetchEntries();
  }, [fetchEntries, debouncedSearchTerm, pagination.currentPage]);

  useEffect(() => { // Update URL search params
    const params = new URLSearchParams();
    if (filters.searchTerm) params.set("search", filters.searchTerm);
    if (filters.status) params.set("status", filters.status);
    if (filters.creatorId) params.set("creatorId", filters.creatorId);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate) params.set("endDate", filters.endDate);
    if (sortBy !== "date") params.set("sortBy", sortBy);
    if (sortDirection !== "desc") params.set("sortDir", sortDirection);
    if (pagination.currentPage !== 0) params.set("page", pagination.currentPage.toString());
    if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());
    setSearchParams(params, { replace: true });
  }, [filters, sortBy, sortDirection, pagination, setSearchParams]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetAllFilters = () => {
    setFilters({ searchTerm: "", status: "", creatorId: "", startDate: "", endDate: "" });
    setSortBy("date"); setSortDirection("desc");
    setPagination(prev => ({ ...prev, currentPage: 0 }));
    // fetchEntries will be called by useEffect on filters/pagination state change if structured that way
    // Or call explicitly if needed after state updates are batched
    // setTimeout(fetchEntries, 0); // Ensure state updates are processed
  };


  const handleSort = (field) => {
    const isAsc = sortBy === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(field);
    setPagination(prev => ({ ...prev, currentPage: 0 }));
  };


  const openActionMenu = (event, entry) => { setActionMenuAnchor(event.currentTarget); setSelectedEntry(entry); };
  const closeActionMenu = () => { setActionMenuAnchor(null); setSelectedEntry(null); };
  const handleViewEntry = (id) => { closeActionMenu(); navigate(`/betriebstagebuch/${id}`); };
  const handleEditEntry = (id) => { closeActionMenu(); navigate(`/betriebstagebuch/${id}/edit`); };

  const openApproveModal = (entry) => {
    setEntryToApprove(entry); setApproveError(null);
    if (currentUser?.signature) { setSignatureData(currentUser.signature); setUseProfileSigForApproval(true); }
    else { setSignatureData(null); setUseProfileSigForApproval(false); if (signatureRef.current) signatureRef.current.clear(); }
    setShowApproveModal(true); closeActionMenu();
  };
  const closeApproveModal = () => { setShowApproveModal(false); setEntryToApprove(null); setApproveLoading(false); setApproveError(null); };

  const handleModalSignatureConfirm = () => {
    if (useProfileSigForApproval && currentUser?.signature) {
      setSignatureData(currentUser.signature);
      setApproveError(null);
      return true;
    }
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setSignatureData(signatureRef.current.getTrimmedCanvas().toDataURL("image/png"));
      setApproveError(null);
      return true;
    }
    setApproveError(t("pleaseProvideSignature"));
    return false;
  };

  const clearModalSignature = () => {
    if (signatureRef.current) signatureRef.current.clear();
    setSignatureData(null); // Clear stored signature data too
    setApproveError(null);
  };

  const handleApprove = async () => {
    if (!handleModalSignatureConfirm()) return; // Validate and set signatureData
    if (!entryToApprove || !signatureData) { setApproveError(t("signatureRequired")); return; }

    setApproveLoading(true); setApproveError(null);
    try {
      await betriebstageBuchService.approve(entryToApprove._id, { reviewerName: currentUser.name, reviewerSignature: signatureData });
      closeApproveModal(); fetchEntries();
    } catch (err) {
      console.error("Error approving entry:", err);
      setApproveError(t("errorApprovingEntry") + `: ${err.response?.data?.message || err.message}`);
    } finally { setApproveLoading(false); }
  };

  const handleQuickView = (entry) => { setCurrentEntry(entry); setShowQuickViewModal(true); };
  async function handleDownloadPdf(entryId) {
    setLoading(true); setError(null);
    try {
      // ...existing code for downloading PDF...
    } catch (err) { setError(t("errorFetchingEntries") + (err.message ? `: ${err.message}` : '')); }
    finally { setLoading(false); }
  }

  const handleChangePage = (event, newPage) => setPagination(prev => ({ ...prev, currentPage: newPage }));
  const handleChangeRowsPerPage = (event) => setPagination(prev => ({ ...prev, pageSize: parseInt(event.target.value, 10), currentPage: 0 }));

  if (loading && entries.length === 0) { /* Initial full page load */ } // Handled by PageContainer return

  console.log('🎨 RENDER STATE:', {
    loading,
    'entries.length': entries.length,
    'entries[0]': entries[0],
    error,
    debouncedSearchTerm,
    'filters.searchTerm': filters.searchTerm
  });

  return (
    <PageContainer title={t("betriebstagebuchList")}>
      <ContentSection title={t("betriebstagebuch")} icon={<ListAltIcon color="primary" />} elevation={0} sx={{ p: 0 }}
        actions={
          <>
            {isAdmin() && <Button component={RouterLink} to="/betriebstagebuch/batch-create" variant="outlined" color="secondary" startIcon={<AddCircleIcon />}>{t("batchGenerate")}</Button>}
            {isManager() && <Button component={RouterLink} to="/betriebstagebuch/create" variant="contained" startIcon={<AddCircleIcon />}>{t("createEntry")}</Button>}
          </>
        }>

        {error && <Alert severity="error" variant="outlined" sx={{ mb: 2 }} onClose={() => setError(null)}><AlertTitle>{t("error")}</AlertTitle>{error}</Alert>}

        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onDateChange={handleDateChange}
          onResetFilters={resetAllFilters}
          uniqueCreators={uniqueCreators}

          isMobile={isMobile}
          t={t}
        />

        {loading && entries.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : entries.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <ListAltIcon sx={{ fontSize: 50, color: 'text.disabled' }} />
            <Typography variant="h6" gutterBottom>{filters.searchTerm || filters.status || filters.creatorId ? t("noMatchingEntries") : t("noEntriesFound")}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{filters.searchTerm || filters.status || filters.creatorId ? t("tryAdjustingFilters") : t("createNewEntryPrompt")}</Typography>
            {isManager() && <Button component={RouterLink} to="/betriebstagebuch/create" variant="contained" startIcon={<AddCircleIcon />}>{t("createEntry")}</Button>}
          </Paper>
        ) : (
          isMobile ? ( /* Mobile Card View */
            <Grid container spacing={2}>
              {entries.map(entry => (
                <Grid item xs={12} key={entry._id}>
                  <Card elevation={1} sx={{ '&:hover': { boxShadow: muiTheme.shadows[3] } }}>
                    <CardHeader
                      avatar={getStatusChip(entry.status, t)}
                      action={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {hasIncompleteChecks(entry) && (
                            <Tooltip title={t("incompleteChecks")}>
                              <WarningIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                          <IconButton size="small" onClick={(e) => openActionMenu(e, entry)}><MoreVertIcon /></IconButton>
                        </Stack>
                      }
                      title={<Typography variant="subtitle2" fontWeight="medium" onClick={() => handleQuickView(entry)} sx={{ cursor: 'pointer' }}>{moment(entry.date).format("LL")}</Typography>}
                      subheader={<Typography variant="caption" color="text.secondary">{t("by")} {entry.creator?.name || t("unknown")}</Typography>}
                      sx={{ pb: 0, pt: 1.5, px: 2 }}
                    />
                    <CardContent sx={{ pt: 0.5, px: 2 }}>
                      {entry.generalNotes && <Typography variant="body2" color="text.secondary" noWrap>{entry.generalNotes}</Typography>}
                    </CardContent>
                    {/* <CardActions sx={{justifyContent:'flex-end', p:1, pt:0}}>
                        <Button size="small" onClick={() => handleQuickView(entry)} startIcon={<VisibilityIcon/>}>{t("view")}</Button>
                    </CardActions> */}
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : ( /* Desktop Table View */
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader size="large">
                <TableHead>
                  <TableRow>
                    {[{ id: 'date', label: t('date') }, { id: 'creator.name', label: t('createdBy') }, { id: 'status', label: t('status') }, { id: 'generalNotes', label: t('summary') }].map(col => (
                      <TableCell key={col.id} sortDirection={sortBy === col.id ? sortDirection : false}>
                        <TableSortLabel active={sortBy === col.id} direction={sortBy === col.id ? sortDirection : 'asc'} onClick={() => handleSort(col.id)} IconComponent={UnfoldMoreIcon}>{col.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center">{t("checks")}</TableCell>
                    <TableCell align="right">{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow hover key={entry._id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }} >
                      <TableCell onClick={() => handleQuickView(entry)} sx={{ cursor: 'pointer' }}><Typography variant="body2">{moment(entry.date).format("L")}</Typography></TableCell>
                      <TableCell onClick={() => handleQuickView(entry)} sx={{ cursor: 'pointer' }}><Typography variant="body2">{entry.creator?.name || t("unknown")}</Typography></TableCell>
                      <TableCell onClick={() => handleQuickView(entry)} sx={{ cursor: 'pointer' }}>{getStatusChip(entry.status, t)}</TableCell>
                      <TableCell onClick={() => handleQuickView(entry)} sx={{ cursor: 'pointer' }}><Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>{entry.generalNotes || '-'}</Typography></TableCell>
                      <TableCell align="center" onClick={() => handleQuickView(entry)} sx={{ cursor: 'pointer' }}>
                        {hasIncompleteChecks(entry) ? (
                          <Tooltip title={t("incompleteChecks")}>
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t("allChecksComplete")}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={t("quickView")}><IconButton size="small" onClick={() => handleQuickView(entry)}><VisibilityIcon /></IconButton></Tooltip>
                          <Tooltip title={t("viewDetails")}><IconButton size="small" component={RouterLink} to={`/betriebstagebuch/${entry._id}`} color="primary"><DescriptionIcon /></IconButton></Tooltip>
                          {isManager() && entry.status !== "approved" && <Tooltip title={t("editEntry")}><IconButton size="small" component={RouterLink} to={`/betriebstagebuch/${entry._id}/edit`} color="warning"><EditIcon /></IconButton></Tooltip>}
                          <Tooltip title={t("downloadPdf")}><IconButton size="small" onClick={() => handleDownloadPdf(entry._id)} color="secondary" disabled={pdfLoadingEntryId === entry._id}>{pdfLoadingEntryId === entry._id ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}</IconButton></Tooltip>
                          {isManager() && entry.status === "pending" && <Tooltip title={t("approveEntry")}><IconButton size="small" onClick={() => openApproveModal(entry)} color="success"><SignIcon /></IconButton></Tooltip>}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
        {entries.length > 0 && <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={pagination.totalItems} rowsPerPage={pagination.pageSize} page={pagination.currentPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage={t("rowsPerPage")} labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t("of")} ${count}`} />}

        {/* Action Menu for Mobile */}
        <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={closeActionMenu} PaperProps={{ sx: { width: 200, borderRadius: muiTheme.shape.borderRadius } }}>
          <MenuItem onClick={() => selectedEntry && handleQuickView(selectedEntry)}><ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon><ListItemText>{t("quickView")}</ListItemText></MenuItem>
          <MenuItem onClick={() => selectedEntry && handleViewEntry(selectedEntry._id)}><ListItemIcon><DescriptionIcon fontSize="small" color="primary" /></ListItemIcon><ListItemText>{t("viewDetails")}</ListItemText></MenuItem>
          {isManager() && selectedEntry?.status !== "approved" && <MenuItem onClick={() => selectedEntry && handleEditEntry(selectedEntry._id)}><ListItemIcon><EditIcon fontSize="small" color="warning" /></ListItemIcon><ListItemText>{t("editEntry")}</ListItemText></MenuItem>}
          <MenuItem onClick={() => { if (selectedEntry) handleDownloadPdf(selectedEntry._id); closeActionMenu(); }}><ListItemIcon><PictureAsPdfIcon fontSize="small" color="secondary" /></ListItemIcon><ListItemText>{t("downloadPdf")}</ListItemText></MenuItem>
          {isManager() && selectedEntry?.status === "pending" && <MenuItem onClick={() => selectedEntry && openApproveModal(selectedEntry)}><ListItemIcon><SignIcon fontSize="small" color="success" /></ListItemIcon><ListItemText>{t("approveEntry")}</ListItemText></MenuItem>}
        </Menu>

        {/* Quick View Dialog */}
        <Dialog open={showQuickViewModal} onClose={() => setShowQuickViewModal(false)} maxWidth="md" fullWidth scroll="paper">
          <DialogTitle sx={{ borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
            {t("betriebstagebuchQuickView")} - {currentEntry?._id?.slice(-8)}
            <IconButton aria-label="close" onClick={() => setShowQuickViewModal(false)} sx={{ position: 'absolute', right: muiTheme.spacing(1), top: muiTheme.spacing(1), color: muiTheme.palette.grey[500] }}><CloseDialogIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            {currentEntry && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("entryDetails")}</Typography>
                    <List dense disablePadding>
                      <ListItem><ListItemText primary={t("date")} secondary={moment(currentEntry.date).format("LL")} /></ListItem>
                      <ListItem><ListItemText primary={t("status")} secondary={getStatusChip(currentEntry.status, t)} /></ListItem>
                      <ListItem><ListItemText primary={t("createdBy")} secondary={currentEntry.creator?.name || t("unknown")} /></ListItem>
                      {currentEntry.reviewerName && <ListItem><ListItemText primary={t("reviewedBy")} secondary={currentEntry.reviewerName} /></ListItem>}
                      <ListItem><ListItemText primary={t("createdAt")} secondary={moment(currentEntry.createdAt).format("LLL")} /></ListItem>
                    </List>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("keyInformation")}</Typography>
                    <List dense disablePadding>
                      <ListItem><ListItemText primary={t("shiftStart")} secondary={currentEntry.shiftStart || '-'} /></ListItem>
                      <ListItem><ListItemText primary={t("shiftEnd")} secondary={currentEntry.shiftEnd || '-'} /></ListItem>
                      <ListItem><ListItemText primary={t("weather")} secondary={currentEntry.weather || '-'} /></ListItem>
                      <ListItem><ListItemText primary={t("temperature")} secondary={currentEntry.temperature ? `${currentEntry.temperature}°C` : '-'} /></ListItem>
                    </List>
                  </Paper>
                </Grid>
                <Grid item xs={12}><Paper variant="outlined" sx={{ p: 2 }}><Typography variant="subtitle1" gutterBottom fontWeight="medium">{t("generalNotes")}</Typography><Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>{currentEntry.generalNotes || t("noNotesRecorded")}</Typography></Paper></Grid>

                {/* Conveyor Checks */}
                {currentEntry.conveyorChecks && currentEntry.conveyorChecks.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ConveyorIcon fontSize="small" />
                        {t("conveyorChecks")}
                      </Typography>
                      <List dense disablePadding>
                        {currentEntry.conveyorChecks.map((check, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {renderCheckStatusIcon(check.functional)}
                                  <Typography variant="body2">{check.name}</Typography>
                                </Box>
                              }
                              secondary={check.notes && <Typography variant="caption" color="text.secondary">{check.notes}</Typography>}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                )}

                {/* Special Checks */}
                {currentEntry.specialChecks && currentEntry.specialChecks.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SpecialCheckIcon fontSize="small" />
                        {t("specialChecks")}
                      </Typography>
                      <List dense disablePadding>
                        {currentEntry.specialChecks.map((check, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {renderCheckStatusIcon(check.completed)}
                                  <Typography variant="body2">{check.name}</Typography>
                                </Box>
                              }
                              secondary={check.notes && <Typography variant="caption" color="text.secondary">{check.notes}</Typography>}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Button onClick={() => setShowQuickViewModal(false)} color="secondary">{t("close")}</Button>
            <Button component={RouterLink} to={`/betriebstagebuch/${currentEntry?._id}`} variant="contained" startIcon={<DescriptionIcon />} onClick={() => setShowQuickViewModal(false)}>{t("viewFullDetails")}</Button>
          </DialogActions>
        </Dialog>

        {/* Approval Dialog */}
        <Dialog open={showApproveModal} onClose={closeApproveModal} maxWidth="xs" fullWidth>
          <DialogTitle>{t("approveEntry")}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1.5 }}>{t("approveEntryConfirmation", { id: entryToApprove?._id.slice(-6) || 'N/A' })}</DialogContentText>
            {approveError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setApproveError(null)}>{approveError}</Alert>}
            {currentUser?.signature && <Button size="small" variant={useProfileSigForApproval ? "contained" : "outlined"} onClick={() => setUseProfileSigForApproval(true)} startIcon={<AccountCircleIcon />} sx={{ mr: 1, mb: 1 }}>{t("useProfileSignature")}</Button>}
            <Button size="small" variant={!useProfileSigForApproval || !currentUser?.signature ? "contained" : "outlined"} onClick={() => setUseProfileSigForApproval(false)} startIcon={<EditIcon />} sx={{ mb: 1 }}>{t("drawSignature")}</Button>

            {useProfileSigForApproval && currentUser?.signature ? (
              <Paper variant="outlined" sx={{ p: 1, mt: 1, textAlign: 'center', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={currentUser.signature} alt={t("profileSignatureAlt")} style={{ maxWidth: "100%", maxHeight: "150px", border: `1px solid ${muiTheme.palette.divider}` }} /></Paper>
            ) : (
              <Paper variant="outlined" sx={{ borderColor: approveError && !useProfileSigForApproval ? "error.main" : "divider", height: 160, mt: 1, position: "relative" }}>
                <SignatureCanvas ref={signatureRef} penColor={muiTheme.palette.text.primary} canvasProps={{ style: { width: "100%", height: "100%" } }} onBegin={() => setApproveError('')} />
                <IconButton onClick={clearModalSignature} size="small" sx={{ position: "absolute", top: 4, right: 4 }}><ClearIcon fontSize="small" /></IconButton>
              </Paper>
            )}
            {approveError && !useProfileSigForApproval && <FormHelperText error sx={{ mt: 0.5 }}>{approveError}</FormHelperText>}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
            <Button onClick={closeApproveModal} color="secondary">{t("cancel")}</Button>
            <Button variant="contained" onClick={handleApprove} disabled={approveLoading} startIcon={approveLoading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}>{t("approve")}</Button>
          </DialogActions>
        </Dialog>

      </ContentSection>
    </PageContainer>
  );
};

export default BetriebstageBuchList;
